import type { Scenario, ScenarioPreview } from "../types/scenario";
import { cn } from "../utils/cn";
import { formatYAxisValue } from "../utils/formatting";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScenarioComparisonProps {
  /** 2–3 scenarios to compare (must have preview data) */
  scenarios: (Scenario & { preview: ScenarioPreview })[];
  /** ID of the currently active scenario */
  activeScenarioId?: string;
  /** Called when user closes the comparison view */
  onClose: () => void;
  /** Called when user wants to view a scenario in detail */
  onViewDetails: (scenarioId: string) => void;
}

// ---------------------------------------------------------------------------
// Metric definitions
// ---------------------------------------------------------------------------

interface MetricDef {
  key: keyof ScenarioPreview;
  label: string;
  /** When true, lower numeric values are "better" */
  lowerIsBetter: boolean;
  format: (value: number, currency: string) => string;
  /** "Best for:" label when this metric is the scenario's strongest */
  bestForLabel: string;
}

const formatPercent = (v: number): string => `${Math.round(v)}%`;

const formatMonthly = (v: number, _currency: string): string => {
  const formatted = formatYAxisValue(v, "CAD");
  return `${formatted}/mo`;
};

const PRIMARY_METRICS: MetricDef[] = [
  {
    key: "retirementAge",
    label: "Retirement Age",
    lowerIsBetter: true,
    format: (v) => String(Math.round(v)),
    bestForLabel: "Early retirement",
  },
  {
    key: "finalValue",
    label: "Net Worth",
    lowerIsBetter: false,
    format: (v, cur) => formatYAxisValue(v, cur),
    bestForLabel: "Maximum wealth",
  },
  {
    key: "monthlyIncome",
    label: "Monthly Income",
    lowerIsBetter: false,
    format: formatMonthly,
    bestForLabel: "Income security",
  },
  {
    key: "readinessScore",
    label: "Readiness Score",
    lowerIsBetter: false,
    format: (v) => formatPercent(v),
    bestForLabel: "Overall retirement readiness",
  },
];

const SUB_SCORE_METRICS: MetricDef[] = [
  {
    key: "shortTermHealthScore",
    label: "Short-Term Health",
    lowerIsBetter: false,
    format: (v) => formatPercent(v),
    bestForLabel: "Short-term stability",
  },
  {
    key: "liquidReservesScore",
    label: "Emergency Fund",
    lowerIsBetter: false,
    format: (v) => formatPercent(v),
    bestForLabel: "Emergency preparedness",
  },
  {
    key: "cashFlowMarginScore",
    label: "Cash Flow Margin",
    lowerIsBetter: false,
    format: (v) => formatPercent(v),
    bestForLabel: "Cash flow stability",
  },
  {
    key: "lifestyleScore",
    label: "Lifestyle",
    lowerIsBetter: false,
    format: (v) => formatPercent(v),
    bestForLabel: "Lifestyle sustainability",
  },
];

const ALL_METRICS = [...PRIMARY_METRICS, ...SUB_SCORE_METRICS];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine the winner index for a metric across scenarios.
 * Returns -1 when all values are equal (no winner).
 */
function determineWinner(values: number[], lowerIsBetter: boolean): number {
  if (values.length === 0) return -1;
  const best = lowerIsBetter ? Math.min(...values) : Math.max(...values);
  // If all equal → no winner
  if (values.every((v) => v === best)) return -1;
  return values.indexOf(best);
}

/**
 * Determine the loser index (worst value).
 * Returns -1 when all values are equal.
 */
function determineLoser(values: number[], lowerIsBetter: boolean): number {
  if (values.length === 0) return -1;
  const worst = lowerIsBetter ? Math.max(...values) : Math.min(...values);
  if (values.every((v) => v === worst)) return -1;
  return values.indexOf(worst);
}

/**
 * Format a delta value.
 */
function formatDelta(
  value: number,
  activeValue: number,
  metric: MetricDef,
  currency: string
): { text: string; isPositive: boolean } {
  const diff = value - activeValue;
  if (diff === 0) return { text: "", isPositive: true };

  const absDiff = Math.abs(diff);
  let formatted: string;

  if (metric.key === "retirementAge") {
    formatted = `${Math.round(absDiff)} yr${absDiff !== 1 ? "s" : ""}`;
  } else if (metric.key === "finalValue" || metric.key === "monthlyIncome") {
    formatted = formatYAxisValue(absDiff, currency);
  } else {
    formatted = `${Math.round(absDiff)}%`;
  }

  // For lowerIsBetter metrics, a negative diff is "positive" (better)
  const isPositive = metric.lowerIsBetter ? diff < 0 : diff > 0;
  const prefix = isPositive ? "+" : "-";
  return { text: `${prefix}${formatted}`, isPositive };
}

/**
 * Get a "best for" label based on the scenario's strongest metric win.
 */
function getBestForLabel(
  scenarioIdx: number,
  scenarios: (Scenario & { preview: ScenarioPreview })[]
): string {
  const metricWins = ALL_METRICS.map((metric) => {
    const values = scenarios.map((s) => s.preview[metric.key] as number);
    return determineWinner(values, metric.lowerIsBetter);
  });

  // Find the metric where this scenario wins that has the highest "importance"
  // (PRIMARY_METRICS first, then sub-scores)
  for (let i = 0; i < ALL_METRICS.length; i++) {
    if (metricWins[i] === scenarioIdx) {
      return ALL_METRICS[i].bestForLabel;
    }
  }
  return "Balanced approach";
}

/**
 * Count metric wins for a scenario across all 8 metrics.
 */
function countWins(
  scenarioIdx: number,
  scenarios: (Scenario & { preview: ScenarioPreview })[]
): number {
  return ALL_METRICS.reduce((count, metric) => {
    const values = scenarios.map((s) => s.preview[metric.key] as number);
    return (
      count +
      (determineWinner(values, metric.lowerIsBetter) === scenarioIdx ? 1 : 0)
    );
  }, 0);
}

/**
 * Convert wins count to 1–5 star rating.
 */
function winsToStars(wins: number, totalMetrics: number): number {
  if (totalMetrics === 0) return 3;
  const ratio = wins / totalMetrics;
  if (ratio >= 0.75) return 5;
  if (ratio >= 0.5) return 4;
  if (ratio >= 0.25) return 3;
  if (ratio > 0) return 2;
  return 1;
}

/**
 * Render star rating as string.
 */
function renderStars(count: number): string {
  return "★".repeat(count) + "☆".repeat(5 - count);
}

/**
 * Get the subscore bar color class.
 */
function getSubScoreBarClass(value: number): string {
  if (value >= 75) return "comparison-subscore-bar--good";
  if (value >= 50) return "comparison-subscore-bar--fair";
  return "comparison-subscore-bar--poor";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScenarioComparison = ({
  scenarios,
  activeScenarioId,
  onClose,
  onViewDetails,
}: ScenarioComparisonProps) => {
  const activeIdx = scenarios.findIndex((s) => s.id === activeScenarioId);

  const renderMetricRow = (metric: MetricDef, isSubScore: boolean) => {
    const values = scenarios.map((s) => s.preview[metric.key] as number);
    const winnerIdx = determineWinner(values, metric.lowerIsBetter);
    const loserIdx = determineLoser(values, metric.lowerIsBetter);

    return (
      <div className="comparison-metric-row" key={metric.key}>
        <div className="comparison-metric-label">{metric.label}</div>
        {scenarios.map((scenario, idx) => {
          const value = scenario.preview[metric.key] as number;
          const isWinner = idx === winnerIdx;
          const isLoser = idx === loserIdx;
          const isNeutral = winnerIdx === -1;
          const isActive = idx === activeIdx;

          const cellClass = cn(
            "comparison-metric-cell",
            isNeutral
              ? "comparison-cell--neutral"
              : isWinner
                ? "comparison-cell--winner"
                : isLoser
                  ? "comparison-cell--loser"
                  : "comparison-cell--neutral",
            isActive ? "scenario-col-baseline" : "scenario-col-alternate"
          );

          // Delta from active scenario
          let deltaEl = null;
          if (activeIdx >= 0) {
            if (isActive) {
              deltaEl = (
                <span className="comparison-metric-delta comparison-metric-delta--current">
                  — current —
                </span>
              );
            } else {
              const activeValue = scenarios[activeIdx].preview[
                metric.key
              ] as number;
              const delta = formatDelta(
                value,
                activeValue,
                metric,
                scenario.preview.currency
              );
              if (delta.text) {
                deltaEl = (
                  <span
                    className={cn(
                      "comparison-metric-delta",
                      delta.isPositive
                        ? "comparison-metric-delta--positive"
                        : "comparison-metric-delta--negative"
                    )}
                  >
                    {delta.text}
                  </span>
                );
              }
            }
          }

          return (
            <div className={cellClass} key={scenario.id}>
              <span className="comparison-metric-value">
                {isWinner && (
                  <span className="comparison-winner-icon" aria-hidden="true">
                    🏆{" "}
                  </span>
                )}
                <span>{metric.format(value, scenario.preview.currency)}</span>
              </span>
              {deltaEl}
              {isSubScore && (
                <div className="comparison-subscore-bar-container">
                  <div
                    className={cn(
                      "comparison-subscore-bar",
                      getSubScoreBarClass(value)
                    )}
                    style={{ width: `${Math.min(value, 100)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="comparison-view" id="scenario-comparison">
      {/* Header */}
      <div className="comparison-header">
        <h2 className="comparison-title">
          Comparing {scenarios.length} Scenarios
        </h2>
        <button
          className="comparison-close-btn"
          onClick={onClose}
          aria-label="Close comparison"
        >
          ✕
        </button>
      </div>

      {/* Column identity headers */}
      <div className="comparison-grid">
        <div className="comparison-header-row">
          <div className="comparison-metric-label" />
          {scenarios.map((scenario) => {
            const isActive = scenario.id === activeScenarioId;
            return (
              <div
                className={cn(
                  "comparison-header-card",
                  isActive && "comparison-header-card--active",
                  isActive ? "scenario-col-baseline" : "scenario-col-alternate"
                )}
                key={scenario.id}
              >
                <div className="comparison-header-name">{scenario.name}</div>
                <div className="comparison-header-subtitle">
                  Retire at {Math.round(scenario.preview.retirementAge)}
                </div>
                {isActive && (
                  <span className="comparison-header-badge">Active</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Primary metrics section */}
        <div className="comparison-section-header">PRIMARY METRICS</div>
        {PRIMARY_METRICS.map((m) => renderMetricRow(m, false))}

        {/* Sub-scores section */}
        <div className="comparison-section-header">READINESS BREAKDOWN</div>
        {SUB_SCORE_METRICS.map((m) => renderMetricRow(m, true))}
      </div>

      {/* View Details CTAs */}
      <div className="comparison-actions-row">
        {scenarios.map((scenario) => (
          <button
            key={scenario.id}
            className="comparison-view-details-btn"
            onClick={() => onViewDetails(scenario.id)}
            aria-label={`View details for ${scenario.name}`}
          >
            View Details
          </button>
        ))}
      </div>

      {/* Overall winner summary */}
      <div className="comparison-summary-row">
        <div className="comparison-summary-label">OVERALL</div>
        <div className="comparison-summary-columns">
          {scenarios.map((scenario, idx) => {
            const wins = countWins(idx, scenarios);
            const stars = winsToStars(wins, ALL_METRICS.length);
            const bestFor = getBestForLabel(idx, scenarios);

            return (
              <div className="comparison-summary-column" key={scenario.id}>
                <div className="comparison-summary-stars">
                  {renderStars(stars)}
                </div>
                <div className="comparison-summary-bestfor">
                  Best for: {bestFor}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
